"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ModelDownloader = void 0;
var logging_1 = require("@augloop/logging/dist/src/client/logging");
var cdn_downloader_1 = require("./cdn-downloader");
var idb_1 = require("idb");
var ModelDownloader = /** @class */ (function () {
    function ModelDownloader(hostApp, baseURL) {
        var _this = this;
        this.dbName = 'ALModels_db';
        this.osName = 'ALModels_os';
        this.cdnDownloader = new cdn_downloader_1.DownloaderFromCDN("https://" + baseURL + "/web/" + hostApp);
        // Start log operation
        var updateMetadataOp = new logging_1.Operation({
            operationName: 'UpdateSessionsSinceModelsUsed',
            success: true,
            resultDescription: ''
        });
        updateMetadataOp.start();
        var id;
        var timeoutPromise = new Promise(function (_resolve, reject) {
            var ms = 500;
            id = setTimeout(function () {
                reject(new Error("Took longer than " + ms + " ms"));
            }, ms);
        });
        var updateTimesSinceUsagePromise = this.accessDB().then(function (db) {
            return db.transaction(_this.osName, 'readwrite').objectStore(_this.osName).openCursor();
        }).then(function updateTimes(cursor) {
            if (!cursor) {
                return;
            }
            var model = cursor.value;
            if (!model.timesSinceUsage) {
                model.timesSinceUsage = 0;
            }
            model.timesSinceUsage++;
            if (model.timesSinceUsage > 5) {
                // Delete unused models
                cursor.delete();
            }
            else {
                // Update number of sessions since model was used
                cursor.update(model);
            }
            return cursor.continue().then(updateTimes);
        });
        this.initPromise = Promise.race([timeoutPromise, updateTimesSinceUsagePromise]).catch(function (err) {
            updateMetadataOp.success = false;
            updateMetadataOp.resultDescription += err.message + " ";
        }).finally(function () {
            clearTimeout(id);
            _this.closeDBAndEndLog(updateMetadataOp, "initializing idb storage");
        });
    }
    ModelDownloader.prototype.getResourceAsArrayBuffer = function (modelId, version) {
        var _this = this;
        // Start log operation
        var getModelABOp = new logging_1.Operation({
            operationName: 'GetModelArrayBuffer',
            success: true,
            resultDescription: ''
        });
        getModelABOp.start();
        var dict = {};
        return this.initPromise.catch(function (err) {
            getModelABOp.success = false;
            getModelABOp.resultDescription += err.message + " ";
        }).then(function () {
            return _this.getIndexedDBFiles(modelId, version, getModelABOp);
        }).then(function (model) {
            return Promise.all(Array.from(model.keys()).map(function (key) {
                var value = model.get(key);
                if (value.arrayBuffer) {
                    return value.arrayBuffer().then(function (abValue) {
                        dict[key] = abValue;
                    });
                }
                else {
                    return new Promise(function (resolve, reject) {
                        var reader = new FileReader();
                        reader.readAsArrayBuffer(value);
                        reader.onload = function (event) {
                            var abVal = event.target.result;
                            if (!(abVal instanceof ArrayBuffer)) {
                                reject(new Error('Unable to obtain ArrayBuffer'));
                            }
                            else {
                                dict[key] = abVal;
                                resolve();
                            }
                        };
                    });
                }
            }));
        }).then(function () {
            return dict;
        }).catch(function (err) {
            getModelABOp.success = false;
            getModelABOp.resultDescription += err.message + " ";
            throw err;
        }).finally(function () {
            _this.closeDBAndEndLog(getModelABOp, "Successfully retrieved " + modelId);
        });
    };
    ModelDownloader.prototype.getResourceAsURL = function (modelId, version) {
        var _this = this;
        // Start log operation
        var getModelURLOp = new logging_1.Operation({
            operationName: 'GetModelURL',
            success: true,
            resultDescription: ''
        });
        getModelURLOp.start();
        var dict = {};
        return this.initPromise.catch(function (err) {
            getModelURLOp.success = false;
            getModelURLOp.resultDescription += err.message + " ";
        }).then(function () {
            return _this.getIndexedDBFiles(modelId, version, getModelURLOp);
        }).then(function (model) {
            return Promise.all(Array.from(model.keys()).map(function (key) {
                // Translate blob to resourcePath
                dict[key] = URL.createObjectURL(model.get(key));
            }));
        }).then(function () {
            return dict;
        }).catch(function (err) {
            getModelURLOp.success = false;
            getModelURLOp.resultDescription += err.message + " ";
            throw err;
        }).finally(function () {
            _this.closeDBAndEndLog(getModelURLOp, "Successfully retrieved " + modelId);
        });
    };
    ModelDownloader.prototype.accessDB = function () {
        var _this = this;
        if (!this.accessDBPromise) {
            this.accessDBPromise = idb_1.openDB(this.dbName, 1, {
                upgrade: function (db) {
                    if (!db.objectStoreNames.contains(_this.osName)) {
                        db.createObjectStore(_this.osName, { keyPath: 'modelId' });
                    }
                },
            });
        }
        return this.accessDBPromise;
    };
    ModelDownloader.prototype.endLog = function (logOp, task) {
        if (logOp.success) {
            logOp.resultDescription = "Success " + task;
            logging_1.Log.info(0x222c504f /* tag_8lfbp */, logging_1.LogCategory.CoreDefault, logOp.stop());
        }
        else {
            logOp.resultDescription += "error " + task;
            logging_1.Log.error(0x222c5050 /* tag_8lfbq */, logging_1.LogCategory.CoreDefault, logOp.stop());
        }
    };
    ModelDownloader.prototype.closeDBAndEndLog = function (logOp, task) {
        var _this = this;
        if (this.accessDBPromise) {
            this.accessDBPromise.then(function (db) {
                try {
                    db.close();
                }
                catch (err) {
                    logOp.success = false;
                    logOp.resultDescription += (err.message ? err.message : 'Error closing db') + " ";
                }
            }).finally(function () {
                _this.accessDBPromise = undefined;
                _this.endLog(logOp, task);
            });
        }
    };
    /**
     * Retrieves files from IDB or, if that does not work, CDN.
     * If error occurs but files are still retrieved, will record error but still resolve with files.
     * @param modelId
     * @param version
     *
     * @returns Model from IDB or CDN.
     */
    ModelDownloader.prototype.getIndexedDBFiles = function (modelId, version, logOp) {
        var _this = this;
        return this.accessDB().then(function (db) {
            // Attempt to retrieve file from IndexedDB
            return db.get(_this.osName, modelId);
        }).then(function (modelObj) {
            // Check if correct version of model
            if (modelObj && modelObj.model && modelObj.version == version) {
                return Promise.resolve(modelObj.model);
            }
            else {
                return Promise.reject(); // Retrieve model from CDN in catch
            }
        }).catch(function (err) {
            if (err) {
                logOp.success = false;
                logOp.resultDescription += err.message + " ";
            }
            return _this.cdnDownloader.downloadFiles(modelId, version);
        }).then(function (model) {
            // Update the timesSinceUsage
            return _this.putModel(modelId, model, version).then(function () {
                return (model);
            }).catch(function (err) {
                // Error putting model in IDB
                logOp.success = false;
                logOp.resultDescription += err.message + " ";
                return (model);
            });
        });
    };
    // Used to put new models in & update times since usage
    ModelDownloader.prototype.putModel = function (modelId, model, version) {
        var _this = this;
        return this.accessDB().then(function (db) {
            return db.put(_this.osName, { modelId: modelId, model: model, version: version, timesSinceUsage: 0 });
        });
    };
    return ModelDownloader;
}());
exports.ModelDownloader = ModelDownloader;
//# sourceMappingURL=model-downloader.js.map